import { useEffect, useState } from "react";
import { fetchDashboard } from "../api";
import type { AppConfig, DashModeSummary } from "../types";

function fmt(v: string) {
  const n = parseFloat(v);
  if (isNaN(n)) return "$0.00";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function pnlColor(v: string) {
  const n = parseFloat(v);
  if (n > 0) return "var(--good)";
  if (n < 0) return "var(--bad)";
  return "var(--text-dim)";
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{
      background: "var(--bg-elev)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius)",
      padding: "16px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    }}>
      <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-dim)" }}>
        {label}
      </span>
      <span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: color ?? "var(--text)" }}>
        {value}
      </span>
      {sub && <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{sub}</span>}
    </div>
  );
}

function ModeRow({ label, s }: { label: string; s: DashModeSummary }) {
  return (
    <tr>
      <td style={{ padding: "10px 10px", fontWeight: 500 }}>{label}</td>
      <td className="td-money" style={{ padding: "10px 10px" }}>{fmt(s.total_invested)}</td>
      <td className="td-money" style={{ padding: "10px 10px" }}>{fmt(s.in_stock_value)}</td>
      <td className="td-money" style={{ padding: "10px 10px" }}>{fmt(s.market_value)}</td>
      <td className="td-money" style={{ padding: "10px 10px", color: pnlColor(s.unrealized_pnl) }}>
        {fmt(s.unrealized_pnl)}
      </td>
      <td className="td-money" style={{ padding: "10px 10px", color: pnlColor(s.realized_pnl) }}>
        {fmt(s.realized_pnl)}
      </td>
      <td style={{ padding: "10px 10px", textAlign: "right", color: "var(--text-dim)", fontSize: 12 }}>
        {s.items_in_stock} in stock · {s.items_sold} sold
      </td>
    </tr>
  );
}

export default function Dashboard({ cfg }: { cfg: AppConfig }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchDashboard>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchDashboard(cfg)
      .then(setData)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [cfg]);

  if (loading) return <div className="state-container"><div className="spinner" /></div>;
  if (err) return <div className="state-container"><div className="state-box"><p style={{ color: "var(--bad)" }}>{err}</p></div></div>;
  if (!data) return null;

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "20px" }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Dashboard</h2>

      {/* Overall stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 28 }}>
        <StatCard label="Total Invested" value={fmt(data.overall.total_invested)} />
        <StatCard label="Market Value" value={fmt(data.overall.market_value)} />
        <StatCard
          label="Unrealized P&L"
          value={fmt(data.overall.unrealized_pnl)}
          color={pnlColor(data.overall.unrealized_pnl)}
        />
        <StatCard
          label="Realized P&L"
          value={fmt(data.overall.realized_pnl)}
          color={pnlColor(data.overall.realized_pnl)}
        />
      </div>

      {/* Per-mode breakdown */}
      <div style={{ background: "var(--bg-elev)", border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", fontSize: 12, fontWeight: 600, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Mode Breakdown
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Mode", "Invested", "Cost Basis (In Stock)", "Market Value", "Unrealized P&L", "Realized P&L", "Items"].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: h === "Mode" ? "left" : "right", color: "var(--text-dim)", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <ModeRow label="Sealed" s={data.sealed} />
              <ModeRow label="Singles" s={data.singles} />
              <ModeRow label="Graded" s={data.graded} />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
